import { useMemo } from "react";

import {
  Badge,
  FontIcon,
  TableColumnDefinition,
  TableRowDefinition,
  TableWithDefaultFilters,
  Typography,
} from "@ezmid/components-library";
import {
  faCheckCircle,
  faEnvelope,
  faFingerprint,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";

import { EntityUser } from "../../api/users.generated";

type Props = {
  isFetching: boolean;
  users: Array<EntityUser | undefined>;
  headerText: string;
};

type TableData = {
  id: string;
  name: string;
  email: string;
  createdAt: string;
  status: string;
};

export function UsersTable<T extends object>({
  isFetching,
  users,
  headerText,
}: Props) {
  const { t } = useTranslation();

  const columns: Array<TableColumnDefinition<TableData>> = useMemo(
    () => [
      {
        Header: t("table.header.name"),
        id: "name",
        accessor: "name",
        Cell: ({ row }: TableRowDefinition<T>) => {
          return (
            <Typography variant="body1">
              {" "}
              {row?.original?.name || ""}
            </Typography>
          );
        },
      },
      {
        Header: t("table.header.userID"),
        Cell: ({ row }: TableRowDefinition<T>) => (
          <Badge
            variant="pill"
            leftIcon={
              <FontIcon
                marginTop={4}
                customIcon={<FontAwesomeIcon size="lg" icon={faFingerprint} />}
              />
            }
          >
            {row?.original?.id?.toUpperCase().substr(0, 8) || ""}
          </Badge>
        ),
        id: "id",
        accessor: "id",
      },
      {
        Header: t("table.header.email"),
        Cell: ({ row }: TableRowDefinition<T>) => (
          <Badge
            variant="pill"
            status="break"
            leftIcon={
              <FontIcon
                marginTop={3}
                customIcon={<FontAwesomeIcon size="lg" icon={faEnvelope} />}
              />
            }
          >
            {row?.original?.email || ""}
          </Badge>
        ),
        id: "email",
        accessor: "email",
      },
      {
        Header: t("table.header.status"),
        Cell: ({ row }: TableRowDefinition<T>) => {
          const isActive = row?.original?.status === "us-active";
          const status = isActive ? "success" : "danger";
          const icon = isActive ? faCheckCircle : faCheckCircle;

          return (
            <Badge
              status={status}
              variant="pill"
              leftIcon={
                <FontIcon
                  marginTop={3}
                  customIcon={<FontAwesomeIcon size="lg" icon={icon} />}
                />
              }
            >
              {t(`status.${row?.original?.status || "unknown"}`)}
            </Badge>
          );
        },
        id: "status",
        accessor: "status",
      },
    ],
    [t]
  );

  const memoizedData: Array<TableData> = useMemo(
    () =>
      users
        .filter((user): user is EntityUser => user !== undefined)
        .map((user) => ({
          id: user?.id || "",
          name: user?.name || "",
          email: user?.email || "",
          createdAt: user?.createdAt || "",
          status: user?.status || "",
        })),
    [users]
  );

  return (
    <div>
      <TableWithDefaultFilters
        tableData={memoizedData}
        tableColumns={columns}
        showPagination={false}
        showToolbar={false}
        headerIcon={
          <FontIcon
            marginTop={4}
            customIcon={<FontAwesomeIcon icon={faUsers} />}
          />
        }
        headerText={headerText}
      />
    </div>
  );
}
